import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const NAV_KEYS = [
  { id: "about", label: "About" },
  { id: "courses", label: "Courses" },
  { id: "consultancy", label: "Consultancy" },
  { id: "whyChooseUs", label: "Why Us" },
  { id: "faculty", label: "Faculty" },
  { id: "gallery", label: "Gallery" },
  { id: "testimonials", label: "Testimonials" },
  { id: "colleges", label: "Colleges" },
  { id: "contact", label: "Contact" },
];

export default function Header({ onEnquire }) {
  const { content } = useContent();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!content) return null;
  const sections = content.sections || {};
  const branding = content.branding || {};
  const navItems = NAV_KEYS.filter((n) => sections[n.id]?.visible !== false);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all ${
        scrolled ? "bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-sm" : "bg-white/70 backdrop-blur-sm"
      }`}
    >
      <div className="container-pad flex items-center justify-between h-16 md:h-20">
        <a href="#top" data-testid="brand-link" className="flex items-center gap-3 group">
          {branding.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={branding.siteName || "Logo"}
              className="h-11 md:h-14 w-auto object-contain group-hover:scale-105 transition"
              data-testid="brand-logo-img"
            />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-[#1E3A8A] grid place-items-center text-white font-bold shadow-sm group-hover:scale-105 transition">
              S
            </div>
          )}
          <div className="leading-tight">
            <div className="font-extrabold text-[#1E3A8A] text-base md:text-lg">{branding.logoText || "Sunbright"}</div>
            <div className="text-[10px] md:text-xs text-slate-500 tracking-wide uppercase">{branding.logoAccent || "Pharma Academy"}</div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {navItems.map((n) => (
            <a
              key={n.id}
              href={`#${n.id}`}
              data-testid={`nav-${n.id}`}
              className="text-sm font-medium text-slate-700 hover:text-[#1E3A8A] transition relative after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-0 after:bg-teal-600 hover:after:w-full after:transition-all"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${branding.primaryPhone || ""}`}
            data-testid="header-call-btn"
            aria-label={`Call ${branding.primaryPhone}`}
            className="hidden md:inline-flex w-11 h-11 rounded-full bg-[#1E3A8A]/8 text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white items-center justify-center transition-all hover:scale-105 shadow-sm hover:shadow-md ring-1 ring-[#1E3A8A]/10"
          >
            <Phone className="w-4 h-4" />
          </a>
          <button
            data-testid="header-enquire-btn"
            onClick={onEnquire}
            className="btn-accent !py-2.5 !px-5 text-xs md:text-sm"
          >
            {content?.nav?.ctaLabel || "Enquire Now"}
          </button>
          <button
            className="lg:hidden p-2 rounded-md text-slate-700"
            onClick={() => setOpen((o) => !o)}
            aria-label="toggle menu"
            data-testid="mobile-menu-btn"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white" data-testid="mobile-menu">
          <div className="container-pad py-4 grid gap-3">
            {navItems.map((n) => (
              <a
                key={n.id}
                href={`#${n.id}`}
                onClick={() => setOpen(false)}
                className="text-sm font-medium text-slate-700 py-1"
              >
                {n.label}
              </a>
            ))}
            <Link to="/admin/login" className="text-xs font-semibold text-slate-400 mt-2">Staff Login</Link>
          </div>
        </div>
      )}
    </header>
  );
}
